import type { InitOptions } from 'i18next'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { defaultAppLanguage } from 'library/constants'

export const defaultNS = 'lp'

type Resources = Record<string, Record<string, Record<string, unknown>>>

// Map of language to its import function for lazy loading
const getLanguageImport = (
    lang: string,
): (() => Promise<{ default: Record<string, Record<string, unknown>> }>) | null => {
    const imports: Record<
        string,
        () => Promise<{ default: Record<string, Record<string, unknown>> }>
    > = {
        ro: () => import('library/i18n/ro.json'),
        en: () => import('library/i18n/en-RO.json'),
    }

    return imports[lang] ?? null
}

// Load only the active language initially
const loadActiveLanguage = async (): Promise<Resources> => {
    const resources: Resources = {}
    const importFn = getLanguageImport(defaultAppLanguage)

    if (importFn) {
        const langModule = await importFn()
        resources[defaultAppLanguage] = langModule.default
    }

    return resources
}

// Lazy load additional language when needed
const loadLanguageBundle = async (lang: string): Promise<void> => {
    if (i18n.hasResourceBundle(lang, defaultNS)) {
        return // Already loaded
    }

    const importFn = getLanguageImport(lang)
    if (importFn) {
        const langModule = await importFn()
        i18n.addResourceBundle(lang, defaultNS, langModule.default[defaultNS], true, true)
    }
}

export const supportedLngs: InitOptions['supportedLngs'] = ['en', 'ro']
export const defaultLanguage = defaultAppLanguage

export const initI18n = async (): Promise<typeof i18n> => {
    const resources = await loadActiveLanguage()

    await i18n.use(initReactI18next).init({
        defaultNS,
        ns: defaultNS,
        lng: defaultLanguage,
        fallbackLng: false,
        resources,
        supportedLngs,
        partialBundledLanguages: true,
    })

    document.documentElement.setAttribute('lang', defaultLanguage)

    // Patch changeLanguage to always preload the language bundle first
    const originalChangeLanguage = i18n.changeLanguage.bind(i18n)
    i18n.changeLanguage = (async (lng?: string, ...args: unknown[]) => {
        if (lng) {
            await loadLanguageBundle(lng)
        }
        return originalChangeLanguage(lng, ...(args as []))
    }) as typeof i18n.changeLanguage

    // Update HTML lang attribute when language changes
    i18n.on('languageChanged', (lng) => {
        document.documentElement.setAttribute('lang', lng)
    })

    return i18n
}

export default i18n
